import { jwtDecode } from "jwt-decode";

import { getCollaborationLinkData } from "./data";

// Codium access gate: usuarios sin sesión solo pueden abrir contenido EXISTENTE
// (salas/escenas compartidas). El lienzo en blanco o una sala inexistente exigen
// login. Gate blando (nivel de app), evaluado ANTES de renderizar para no
// mostrar el editor a visitantes no autenticados.

const UNAUTHORIZED_MESSAGE =
  "Tu cuenta de GitHub no está autorizada para crear paneles en Codium.";

export const isLoggedIn = (): boolean => {
  const token = localStorage.getItem("token");
  if (!token) {
    return false;
  }
  try {
    // jwt-decode maneja base64url correctamente (atob no).
    const decoded = jwtDecode<{ exp?: number }>(token);
    return typeof decoded.exp === "number" && decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

// Comprueba si una sala de colaboración EXISTE en el backend. Usa el endpoint
// REST `documents:batchGet` (el único que implementa el backend self-hosted),
// NO el SDK de Firebase: su getDoc abre un canal WebChannel "Listen" que el
// backend no sirve (404) y haría fallar siempre la comprobación. El contenido va
// cifrado con la roomKey; aquí solo nos interesa si existe, no descifrarlo.
const roomExists = async (roomId: string): Promise<boolean> => {
  let projectId = "";
  try {
    projectId = JSON.parse(import.meta.env.VITE_APP_FIREBASE_CONFIG).projectId;
  } catch {
    return false;
  }
  if (!projectId) {
    return false;
  }
  // OJO: el nombre de recurso del documento NO lleva prefijo `/v1/` (ese prefijo
  // es solo del path HTTP). Debe coincidir EXACTAMENTE con la key que guarda el
  // SDK de Firebase al hacer commit, o el backend responde `missing`.
  const docs = `projects/${projectId}/databases/(default)/documents`;
  const name = `${docs}/scenes/${roomId}`;
  try {
    const res = await fetch(`/v1/${docs}:batchGet`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ documents: [name] }),
    });
    if (!res.ok) {
      return false;
    }
    const data = await res.json();
    return Array.isArray(data) && data.some((entry) => entry && entry.found);
  } catch {
    return false;
  }
};

// Decide el acceso ANTES de renderizar. Devuelve false y redirige a login cuando
// un visitante anónimo intenta llegar al lienzo en blanco o a una sala
// inexistente. Devuelve true (renderizar) para usuarios logueados, salas/escenas
// existentes, o un rebote de login rechazado (?auth_error), que la app muestra
// luego con consumeAuthError.
export const canAccessApp = async (): Promise<boolean> => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("auth_error") === "unauthorized") {
    return true;
  }
  if (params.get("token")) {
    // Volvemos del callback de login: el token va en la URL y useAuth lo
    // persistirá en localStorage. Dejar pasar (si no, se redirige a login en
    // bucle porque aún no está en localStorage).
    return true;
  }
  if (isLoggedIn()) {
    return true;
  }
  const roomLinkData = getCollaborationLinkData(window.location.href);
  if (roomLinkData && (await roomExists(roomLinkData.roomId))) {
    return true;
  }
  const hash = window.location.hash;
  if (/^#json=/.test(hash) || /^#url=/.test(hash)) {
    return true;
  }
  window.location.href = "/auth/login";
  return false;
};

// Si el visitante vuelve de un login rechazado, muestra el mensaje y limpia el
// query param. Devuelve true si mostró mensaje.
export const consumeAuthError = (
  setErrorMessage: (message: string) => void,
): boolean => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("auth_error") !== "unauthorized") {
    return false;
  }
  setErrorMessage(UNAUTHORIZED_MESSAGE);
  params.delete("auth_error");
  const qs = params.toString();
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
  );
  return true;
};
